import { useMemo, useState } from "react";
import { complianceApi } from "@/lib/api";
import { Scales, CheckCircle, Warning, ArrowsClockwise, Plus, Trash, Truck } from "@phosphor-icons/react";
import { toast } from "sonner";

/* Realistic interstate axle configurations (49 CFR 658.17 reference) */
const PRESETS = [
  {
    label: "5-Axle Semi (Tractor + Tandem Trailer)",
    axles: [
      { label: "Steer", weight_lbs: 12000, spacing_ft: 0 },
      { label: "Drive 1", weight_lbs: 17000, spacing_ft: 17.5 },
      { label: "Drive 2", weight_lbs: 17000, spacing_ft: 4.5 },
      { label: "Trailer 1", weight_lbs: 17000, spacing_ft: 30.0 },
      { label: "Trailer 2", weight_lbs: 17000, spacing_ft: 4.5 },
    ],
  },
  {
    label: "Tri-axle Dump (Spread)",
    axles: [
      { label: "Steer", weight_lbs: 14000, spacing_ft: 0 },
      { label: "Drive 1", weight_lbs: 20000, spacing_ft: 14.0 },
      { label: "Drive 2", weight_lbs: 20000, spacing_ft: 4.5 },
      { label: "Pusher", weight_lbs: 16000, spacing_ft: 4.5 },
    ],
  },
  {
    label: "Overloaded Tandem (Violation)",
    axles: [
      { label: "Steer", weight_lbs: 12000, spacing_ft: 0 },
      { label: "Drive 1", weight_lbs: 22000, spacing_ft: 16.0 },
      { label: "Drive 2", weight_lbs: 22000, spacing_ft: 4.0 },
    ],
  },
];

function NumberCell({ value, onChange, suffix, step = 0.5, min = 0, testId }) {
  return (
    <div className="flex items-center gap-1">
      <input
        type="number"
        value={value}
        step={step}
        min={min}
        onChange={(e) => onChange(e.target.value === "" ? 0 : Number(e.target.value))}
        className="w-20 px-2 py-1 bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#ff8c00] outline-none text-xs font-mono text-right"
        data-testid={testId}
      />
      {suffix && <span className="text-[10px] uppercase tracking-[0.2em] text-[#71717a]">{suffix}</span>}
    </div>
  );
}

export default function BridgeFormulaCalc() {
  const [axles, setAxles] = useState(PRESETS[0].axles);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [maxGross, setMaxGross] = useState(80000);

  const gross = useMemo(() => axles.reduce((a, b) => a + (Number(b.weight_lbs) || 0), 0), [axles]);
  const wheelbase = useMemo(() => axles.slice(1).reduce((a, b) => a + (Number(b.spacing_ft) || 0), 0), [axles]);

  const update = (idx, field, value) => {
    setAxles((cur) => cur.map((a, i) => (i === idx ? { ...a, [field]: value } : a)));
  };

  const addAxle = () => {
    if (axles.length >= 9) return;
    setAxles((cur) => [...cur, { label: `Axle ${cur.length + 1}`, weight_lbs: 12000, spacing_ft: 4.5 }]);
  };

  const removeAxle = (idx) => {
    if (axles.length <= 2) return;
    setAxles((cur) => cur.filter((_, i) => i !== idx).map((a, i) => (i === 0 ? { ...a, spacing_ft: 0 } : a)));
  };

  const run = async () => {
    setBusy(true);
    try {
      const data = await complianceApi.bridgeFormula({
        axles: axles.map((a, i) => ({
          label: a.label,
          weight_lbs: Math.max(0, Math.round(Number(a.weight_lbs) || 0)),
          spacing_ft: i === 0 ? 0 : Math.max(0, Number(a.spacing_ft) || 0),
        })),
        max_gross_lbs: Math.max(1000, Math.round(Number(maxGross) || 80000)),
      });
      setResult(data);
      if (data.is_compliant) toast.success("Federal Bridge: Compliant");
      else toast.error(`Bridge Formula: ${data.warnings.length} violation${data.warnings.length === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bridge Formula check failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-[#0d0d0d] border border-[#2a2a2a]" data-testid="bridge-formula-calc">
      <div className="border-b border-[#2a2a2a] p-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Truck size={22} className="text-[#4682b4]" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#71717a]">49 CFR § 658.17 · Federal Bridge Formula B</div>
            <h2 className="font-heading font-black text-xl tracking-tight">Per-Axle Bridge Compliance</h2>
          </div>
        </div>
        <div className="hidden md:flex items-center gap-2 font-mono text-xs text-[#71717a]">
          <span>W = 500 × (LN/(N−1) + 12N + 36)</span>
        </div>
      </div>

      <div className="p-5 space-y-5">
        {/* Presets + gross config */}
        <div className="flex flex-wrap items-center gap-2 justify-between">
          <div className="flex flex-wrap gap-2">
            {PRESETS.map((p, i) => (
              <button
                key={p.label}
                onClick={() => { setAxles(p.axles); setResult(null); }}
                className="px-3 py-1.5 border border-[#4682b4] text-[#4682b4] hover:bg-[#4682b4] hover:text-white text-[10px] uppercase tracking-[0.2em] font-bold transition-colors"
                data-testid={`fbf-preset-${i}`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-[0.2em] text-[#71717a]">Max Gross</span>
            <NumberCell value={maxGross} onChange={setMaxGross} suffix="LBS" step={500} testId="fbf-max-gross" />
          </div>
        </div>

        {/* Axle table */}
        <div className="border border-[#2a2a2a]">
          <div className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-[#2a2a2a] bg-[#1a1a1a] text-[10px] uppercase tracking-[0.2em] text-[#71717a] font-bold">
            <div className="col-span-1">#</div>
            <div className="col-span-3">Label</div>
            <div className="col-span-3">Weight</div>
            <div className="col-span-3">Spacing (prev)</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>
          {axles.map((a, idx) => (
            <div
              key={idx}
              className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-[#2a2a2a] last:border-b-0 items-center"
              data-testid={`fbf-axle-row-${idx}`}
            >
              <div className="col-span-1 font-mono text-xs text-[#a1a1aa]">A{idx + 1}</div>
              <div className="col-span-3">
                <input
                  value={a.label || ""}
                  onChange={(e) => update(idx, "label", e.target.value)}
                  className="w-full px-2 py-1 bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#ff8c00] outline-none text-xs"
                  data-testid={`fbf-axle-label-${idx}`}
                />
              </div>
              <div className="col-span-3">
                <NumberCell
                  value={a.weight_lbs}
                  onChange={(v) => update(idx, "weight_lbs", v)}
                  suffix="LBS"
                  step={500}
                  testId={`fbf-axle-weight-${idx}`}
                />
              </div>
              <div className="col-span-3">
                {idx === 0 ? (
                  <span className="text-[10px] uppercase tracking-[0.2em] text-[#71717a] font-mono">Reference</span>
                ) : (
                  <NumberCell
                    value={a.spacing_ft}
                    onChange={(v) => update(idx, "spacing_ft", v)}
                    suffix="FT"
                    step={0.5}
                    testId={`fbf-axle-spacing-${idx}`}
                  />
                )}
              </div>
              <div className="col-span-2 text-right">
                <button
                  onClick={() => removeAxle(idx)}
                  disabled={axles.length <= 2}
                  className="w-7 h-7 border border-[#2a2a2a] hover:border-[#ef4444] hover:text-[#ef4444] disabled:opacity-30 inline-flex items-center justify-center"
                  data-testid={`fbf-axle-remove-${idx}`}
                  title="Remove axle"
                >
                  <Trash size={11} />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Summary + actions */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-5 font-mono text-xs text-[#a1a1aa]">
            <div>
              <span className="text-[#71717a] uppercase tracking-wider text-[10px] mr-1">Gross:</span>
              <span className="text-white font-bold" data-testid="fbf-gross">{gross.toLocaleString()} lbs</span>
            </div>
            <div>
              <span className="text-[#71717a] uppercase tracking-wider text-[10px] mr-1">Wheelbase:</span>
              <span className="text-white font-bold" data-testid="fbf-wheelbase">{wheelbase.toFixed(1)} ft</span>
            </div>
            <div>
              <span className="text-[#71717a] uppercase tracking-wider text-[10px] mr-1">Axles:</span>
              <span className="text-white font-bold">{axles.length}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={addAxle}
              disabled={axles.length >= 9}
              className="px-3 py-2 border border-[#4682b4] text-[#4682b4] hover:bg-[#4682b4] hover:text-white text-[10px] uppercase tracking-[0.2em] font-bold disabled:opacity-40 flex items-center gap-1"
              data-testid="fbf-add-axle"
            >
              <Plus size={11} weight="bold" /> Add Axle
            </button>
            <button
              onClick={run}
              disabled={busy}
              className="px-5 py-2 bg-[#ff8c00] text-black hover:bg-[#e67e00] disabled:opacity-50 text-[10px] uppercase tracking-[0.2em] font-bold flex items-center gap-2"
              data-testid="fbf-run"
            >
              {busy ? <ArrowsClockwise size={12} className="animate-spin" /> : <Scales size={12} weight="bold" />}
              Run Bridge Check
            </button>
          </div>
        </div>

        {/* Result */}
        {result && <BridgeResult result={result} />}
      </div>
    </div>
  );
}

function BridgeResult({ result }) {
  const failingGroups = result.groups.filter((g) => !g.ok);
  const failingGroupsToShow = failingGroups.length ? failingGroups : result.groups.slice(0, 5);

  return (
    <div className="border-t border-[#2a2a2a] pt-5 space-y-4" data-testid="fbf-result">
      {/* Headline */}
      <div
        className="p-4 border-l-4 flex items-start gap-3"
        style={{
          background: result.is_compliant ? "rgba(34,197,94,0.08)" : "rgba(239,68,68,0.08)",
          borderLeftColor: result.is_compliant ? "#22c55e" : "#ef4444",
        }}
      >
        {result.is_compliant ? (
          <CheckCircle size={28} weight="fill" className="text-[#22c55e] mt-0.5" />
        ) : (
          <Warning size={28} weight="fill" className="text-[#ef4444] mt-0.5 ll-status-blink" />
        )}
        <div className="flex-1">
          <div
            className="text-[10px] uppercase tracking-[0.25em]"
            style={{ color: result.is_compliant ? "#22c55e" : "#ef4444" }}
          >
            {result.is_compliant ? "Bridge Formula PASS" : "Bridge Formula FAIL"}
          </div>
          <div className="font-heading font-black text-lg tracking-tight">
            {result.is_compliant
              ? `${result.axle_count} axles, ${result.wheelbase_ft.toFixed(1)} ft wheelbase`
              : result.worst_group && !result.worst_group.ok
              ? `Worst: A${result.worst_group.from_axle}–A${result.worst_group.to_axle} over by ${result.worst_group.over_lbs.toLocaleString()} lbs`
              : "Violation"}
          </div>
        </div>
        <div className="text-right text-xs font-mono text-[#a1a1aa]">
          <div>{result.gross_lbs.toLocaleString()} / {result.max_gross_lbs.toLocaleString()} lbs</div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717a]">Gross / Max</div>
        </div>
      </div>

      {/* Single-axle violations */}
      {result.single_axle_violations.length > 0 && (
        <div className="border border-[#ef4444]/40 bg-[#ef4444]/5 p-3">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#ef4444] mb-2 flex items-center gap-1">
            <Warning size={12} weight="fill" /> Single-axle violations
          </div>
          <ul className="text-xs space-y-1 font-mono">
            {result.single_axle_violations.map((v) => (
              <li key={v.axle} className="text-[#ef4444]">
                Axle {v.axle}: {v.weight_lbs.toLocaleString()} lbs (limit {v.limit_lbs.toLocaleString()}, over {v.over_lbs.toLocaleString()})
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Top groups breakdown */}
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#71717a] mb-2">
          {failingGroups.length ? `Failing axle groups (${failingGroups.length})` : "Sample axle groups"}
        </div>
        <div className="border border-[#2a2a2a]">
          <div className="grid grid-cols-12 gap-1 px-3 py-1.5 border-b border-[#2a2a2a] bg-[#1a1a1a] text-[10px] uppercase tracking-[0.2em] text-[#71717a] font-bold">
            <div className="col-span-2">Axles</div>
            <div className="col-span-2 text-right">Length (ft)</div>
            <div className="col-span-3 text-right">Actual</div>
            <div className="col-span-3 text-right">Max (FBF)</div>
            <div className="col-span-2 text-right">Status</div>
          </div>
          {failingGroupsToShow.map((g, idx) => (
            <div
              key={idx}
              className="grid grid-cols-12 gap-1 px-3 py-1.5 border-b border-[#2a2a2a] last:border-b-0 text-xs font-mono items-center"
              data-testid={`fbf-group-${idx}`}
            >
              <div className="col-span-2">A{g.from_axle}–A{g.to_axle} ({g.axles})</div>
              <div className="col-span-2 text-right">{g.length_ft.toFixed(1)}</div>
              <div className="col-span-3 text-right">{g.weight_lbs.toLocaleString()}</div>
              <div className="col-span-3 text-right">{g.max_lbs.toLocaleString()}</div>
              <div className="col-span-2 text-right">
                <span
                  className="px-1.5 py-0.5 text-[10px] uppercase tracking-[0.2em] font-bold"
                  style={{
                    color: g.ok ? "#22c55e" : "#ef4444",
                    background: g.ok ? "rgba(34,197,94,0.12)" : "rgba(239,68,68,0.12)",
                    border: `1px solid ${g.ok ? "#22c55e" : "#ef4444"}`,
                  }}
                >
                  {g.ok ? "OK" : `+${g.over_lbs.toLocaleString()}`}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Warnings list */}
      {result.warnings.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#ef4444] mb-2 flex items-center gap-1">
            <Warning size={12} weight="fill" /> Warnings
          </div>
          <ul className="text-xs space-y-1">
            {result.warnings.map((w, i) => (
              <li key={i} className="text-[#ef4444] flex items-start gap-2">
                <span>—</span> {w}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
